import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { AkamaiImageQualityEnum } from '@inter-ikea-kompis/enums';
import { DebounceManager } from '@inter-ikea-kompis/utilities';
import { ImageUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Skeleton as SkapaSkeleton } from '@ingka/skeleton-webc/passive.js';

import ImageAspectRatioEnum from '../../enums/ImageAspectRatioEnum.js';
import ImageStyle from './ImageStyle.js';

const TRANSPARENT_PIXEL_DATA_URL =
	'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

/**
 *
 * Image class.
 */

export default class Image extends KompisElement {
	// Image source.
	@property({ type: String })
	public src: string | null = null;

	// Alternative text for the image.
	@property({ type: String })
	public alt: string | null = null;

	// The aspect ratio that the image should adopt.
	@property({ type: String })
	public aspectRatio: ImageAspectRatioEnum = ImageAspectRatioEnum.ORIGINAL;

	// The quality of the image compression done in Akamai. This is currently only supported by images under "/addon-app/".
	@property({ type: String })
	public quality: AkamaiImageQualityEnum | null = null;

	// 	Applies cropping to the image in order to prevent white borders on certain product images. The image will also be placed in a square container, while still keeping its original aspect ratio.
	@property({ type: Boolean })
	public productImage = false;

	// 	Adds a square skeleton animation while the image is loading.
	@property({ type: Boolean })
	public enableSkeletonLoading = false;

	private resizeObserver = new ResizeObserver(this.onResize.bind(this));
	private debounce = new DebounceManager();
	private currentSrc: string | null = null;
	private imageWidth = 0;
	private loadingImage: HTMLImageElement | null = null;
	private loadedSrc: string | null = null;
	private listeners = {
		onResize: this.onResize.bind(this)
	};

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		const styleOptions = {
			isProductImage: this.productImage,
			isLoading: !!this.loadingImage,
			aspectRatio: this.aspectRatio,
			offsetWidth: this.offsetWidth
		};

		return html`
			<style>
				${ImageStyle.getStyle(styleOptions)}
			</style>
			${this.getImage()}
		`;
	}

	/**
	 * Triggered when the component is inserted to DOM.
	 */
	public override async connectedCallback(): Promise<void> {
		this.customElements.define('skapa-skeleton', SkapaSkeleton);

		super.connectedCallback();
		/**
		 *	This listener was added because of a bug with ResizeListener in Chromium.
		 *	It should be removed when Chromium fixes this bug.
		 */
		window.addEventListener('resize', this.listeners.onResize);
		this.resizeObserver.observe(this);
	}

	/**
	 * Triggered when the component is removed from DOM.
	 */
	public override disconnectedCallback(): void {
		super.disconnectedCallback();

		window.removeEventListener('resize', this.listeners.onResize);

		this.resizeObserver.unobserve(this);
		this.debounce.clearAll();
		this.loadedSrc = null;

		if (this.loadingImage) {
			this.loadingImage.onload = null;
			this.loadingImage.onerror = null;
			this.loadingImage = null;
		}
	}

	/**
	 * Returns skeleton HTML.
	 *
	 * @returns Template.
	 */
	private getSkeleton(): RenderResult {
		if (!this.loadingImage || !this.enableSkeletonLoading) {
			return null;
		}

		const imageWidth = this.getImageWidth();
		return html`<skapa-skeleton width="${imageWidth}px" height="${imageWidth}px"></skapa-skeleton>`;
	}

	/**
	 * Returns image HTML.
	 *
	 * @returns Template.
	 */
	private getImage(): RenderResult {
		const src = this.getSrc();
		if (!src) {
			return null;
		}

		this.loadImage(src);

		const skeleton = this.getSkeleton();

		return skeleton ?? html`<img alt="${this.alt ? this.alt : ''}" src="${src}" />`;
	}

	/**
	 * Loads an image.
	 *
	 * @param src Source.
	 */
	private loadImage(src: string): void {
		if (src !== this.loadedSrc) {
			if (this.loadingImage) {
				this.loadingImage.onload = null;
				this.loadingImage.onerror = null;
				this.loadingImage = null;
			}

			this.loadedSrc = src;
			this.loadingImage = new window.Image();
			this.loadingImage.onload = () => this.onLoad();
			this.loadingImage.onerror = () => this.onLoad();
			this.loadingImage.src = src;
		}
	}

	/**
	 * Handles img load event.
	 */
	private onLoad(): void {
		this.loadingImage = null;
		this.update();
	}

	/**
	 * Listener for resize events.
	 */
	private onResize(): void {
		if (this.offsetWidth !== 0) {
			this.debounce.set('resize', () => this.update(), 10);
		}
	}

	/**
	 * Returns the image width.
	 *
	 * Calculating offset width is a quite expensive operation, so we only want to do it once when needed.
	 *
	 * @returns Image width in px.
	 */
	private getImageWidth(): number {
		return this.productImage ? this.offsetWidth * 1.14 : this.offsetWidth;
	}

	/**
	 * Returns the source of the image.
	 *
	 * @returns Source.
	 */
	private getSrc(): string | null {
		if (!this.src || this.src.startsWith('data:')) {
			return this.src;
		}

		const imageWidth = this.getImageWidth();

		if (!imageWidth) {
			// In order for the image to still get a size, we will return a transparent pixel.
			return TRANSPARENT_PIXEL_DATA_URL;
		}

		if (imageWidth > this.imageWidth || this.src !== this.currentSrc) {
			this.imageWidth = imageWidth;
			this.currentSrc = this.src;
			return ImageUtility.getAkamaiImageUrl(this.src, imageWidth, this.quality ?? undefined);
		}

		return ImageUtility.getAkamaiImageUrl(this.src, this.imageWidth, this.quality ?? undefined);
	}
}
declare global {
	interface HTMLElementTagNameMap {
		'skapa-skeleton': SkapaSkeleton;
		'kompis-image': Image;
	}
}
